import axios from 'axios';
import { stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import { CamelCaseCodeInput } from 'components/final-form/Inputs';
import Select from 'components/final-form/Select';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { useRef, useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addLabTestComponent, editLabTestComponent } from 'services/labTestComponentService';
import styled from 'styled-components';
import { ComponentValueEntryPoint, LabTestResultType, ScaleType } from 'ts-types/api.enums';
import { UpsertLabTestComponentDto } from 'ts-types/api.types';
import { required } from 'utils/validatorUtils';


const UpsertLabTestComponentFormContent = styled(FormContent)`
  max-width: 570px;
`;

interface Props extends MessageSourceProps {
  labTestComponentId?: number,
  labTestComponent?: UpsertLabTestComponentDto,
  onCancel: () => void,
  onSave: () => void
}

const cancelTokenSource = axios.CancelToken.source();

const UpsertLabTestComponentForm = (props: Props) => {

  const {labTestComponentId, labTestComponent, onCancel, onSave, getMessage} = props;

  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const valueEntryPointLabels: Array<string> = Object.values(ComponentValueEntryPoint);
  const valueEntryPoints = stringArrayToDropdownOptionArray(
      valueEntryPointLabels, getMessage, "", valueEntryPointLabels);

  const resultTypesLabels: Array<string> = Object.values(LabTestResultType);
  const labTestResultTypes = stringArrayToDropdownOptionArray(
      resultTypesLabels, getMessage, "", resultTypesLabels);

  const scaleTypes: Array<string> = Object.values(ScaleType);
  const scaleTypeOptions = stringArrayToDropdownOptionArray(
      scaleTypes, getMessage, "enum.scaleType", scaleTypes);

  const initialValues = useRef<Partial<UpsertLabTestComponentDto>>(
      labTestComponent || {valueEntryPoint: "TEST_RESULT"})

  const handleSubmit = async (values: UpsertLabTestComponentDto) => {

    let saveValues: UpsertLabTestComponentDto = {...values}

    const handleError = (error: any) => {
      setErrorMessage(error.response.data.violations[0].details);
    };

    if (labTestComponentId) {
      await editLabTestComponent(labTestComponentId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(e => handleError(e));
    } else {
      await addLabTestComponent(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(e => handleError(e));
    }

  }

  const renderFinalForm = (): React.ReactNode => {

    return (
        <FinalForm
            onSubmit={(values: any) => handleSubmit(values)}
            initialValues={initialValues.current}
            subscription={{values: true, pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  }

  const renderForm = ({values, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {


    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <UpsertLabTestComponentFormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.code")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="code"
                        component={CamelCaseCodeInput}
                        validate={required}
                        disabled={labTestComponentId !== undefined}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.name")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="name"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.valueEntryPoint")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="valueEntryPoint"
                        component={Select}
                        options={valueEntryPoints}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.resultType")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="resultType"
                        component={Select}
                        options={labTestResultTypes}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.scale")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="scale"
                        component={Select}
                        options={scaleTypeOptions}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.exampleUnits")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="exampleUnits"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.referenceValueFrom")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="referenceValueFrom"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("labTestComponent.referenceValueTo")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="referenceValueTo"
                        component={Input}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </UpsertLabTestComponentFormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  return (
      <>
        {renderFinalForm()}
      </>
  )

}

export default withMessages(UpsertLabTestComponentForm);
